import BuildRoundedIcon from '@mui/icons-material/BuildRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import {
  Box,
  Button,
  Divider,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import CompleteModuleUtility from './UtilityViews/CompleteModuleUtility';
import DeleteUserUtility from './UtilityViews/DeleteUserUtility';
import OnboardingStepsUtility from './UtilityViews/OnboardingStepsUtility';

/**
 * The subview on the admin dashboard for utilities
 */
const UtilitiesSubview = () => {
  const [anchorElement, setAnchorElement] = useState<Element | null>(null);
  const [selectedUtility, setSelectedUtility] = useState('Select Utility');
  const [utilityComponent, setUtilityComponent] = useState<JSX.Element>();

  /**
   * The utilities available to the user. Keep in alphabetical order
   */
  const utilities = useMemo<Record<string, JSX.Element>>(
    () => ({
      'Edit Completed Modules': <CompleteModuleUtility />,
      'Edit Onboarding Steps': <OnboardingStepsUtility />,
      'Delete User': <DeleteUserUtility />,
    }),
    []
  );

  /**
   * Handle when the select dropdown is closed
   */
  const handleClose = useCallback(() => {
    setAnchorElement(null);
  }, []);

  /**
   * Handle when the select utility button was clicked
   */
  const handleSelectUtility = useCallback(
    (menuUtility: string) => {
      // Set the selected utility
      setSelectedUtility(menuUtility);

      // Switch the utility
      setUtilityComponent(utilities[menuUtility]);

      // Close the menu
      handleClose();
    },
    [handleClose, utilities]
  );

  return (
    <Box height="100%" display="flex" flexDirection="column">
      <Box display="flex" justifyContent="space-between" padding={1.5}>
        <Box display="flex">
          <Typography variant="h4">
            Utilities{utilityComponent ? ` / ${selectedUtility}` : ''}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          color="secondary"
          onClick={(event) => setAnchorElement(event.currentTarget)}
          startIcon={<ExpandMoreRoundedIcon />}
          endIcon={<BuildRoundedIcon />}
        >
          {selectedUtility}
        </Button>
        <Menu
          anchorEl={anchorElement}
          keepMounted
          open={Boolean(anchorElement)}
          onClose={handleClose}
        >
          {Object.keys(utilities).map((k) => (
            <MenuItem key={k} onClick={() => handleSelectUtility(k)}>
              {k}
            </MenuItem>
          ))}
        </Menu>
      </Box>
      <Divider />
      <Box paddingX="1em" paddingY=".5em" height="100%">
        {utilityComponent ?? (
          <>
            <Typography variant="h4" component="h1">
              Welcome to your admin utilities page
            </Typography>
            <Typography variant="body1">
              Select a utility in the upper right hand corner to get started
            </Typography>
          </>
        )}
      </Box>
    </Box>
  );
};

export default UtilitiesSubview;
